/**
 * FSRS-4.5 scheduler (per-clue memory model).
 *
 * Free Spaced Repetition Scheduler — fits a memory model with three latent
 * variables per card: difficulty (D, 1..10), stability (S, days), and the
 * derived retrievability (R, probability of recall now). Weights are the
 * published FSRS-4.5 defaults; they can later be re-fit per user from the
 * review log, but the defaults are strong out of the box.
 *
 * Reference: Jarrett Ye et al., "A Stochastic Shortest Path Algorithm for
 * Optimizing Spaced Repetition Scheduling" (arXiv:2402.05134) and the
 * fsrs4anki wiki. Implementation is original (MIT-compatible).
 *
 * The app exposes a 3-button self-grade (Missed / Shaky / Known). FSRS expects
 * a 4-point rating (Again / Hard / Good / Easy). We map:
 *   Missed -> Again (1)
 *   Shaky  -> Hard  (2)
 *   Known  -> Good  (3)
 * We never emit Easy (4) from a tap; "Easy" is reserved for a future
 * long-press gesture. This keeps the grading honest — most "knew it" recalls
 * on a trivia item are Good, not Easy.
 */
import { Grade, type CardState } from './types';

// FSRS-4.5 default weights (w0..w18).
const W = [
  0.4072, 1.1829, 3.1262, 15.4722, 7.2102, 0.5316, 1.0651, 0.0234, 1.616,
  0.1544, 1.0824, 1.9813, 0.0953, 0.2975, 2.2042, 0.2407, 2.9466, 0.5034, 0.6567,
] as const;

const DECAY = -0.5;
const FACTOR = Math.pow(0.9, 1 / DECAY) - 1; // ≈ 0.2346
const MIN_STABILITY = 0.01;
export const DAY_MS = 86_400_000;

/** FSRS rating 1..4. */
type Rating = 1 | 2 | 3 | 4;

function gradeToRating(g: Grade): Rating {
  switch (g) {
    case Grade.Missed:
      return 1;
    case Grade.Shaky:
      return 2;
    case Grade.Known:
      return 3;
  }
}

const clampD = (d: number) => Math.min(Math.max(d, 1), 10);

/** Retrievability after `elapsedDays` given stability S. */
export function retrievability(elapsedDays: number, stability: number): number {
  return Math.pow(1 + (FACTOR * elapsedDays) / stability, DECAY);
}

/** Initial difficulty for a card first rated `rating`. */
function initDifficulty(rating: Rating): number {
  return clampD(W[2]! - Math.exp(W[3]! * (rating - 1)) + 1);
}

/** Initial stability for a card first rated `rating`. */
function initStability(rating: Rating): number {
  return Math.max(W[rating - 1]!, MIN_STABILITY);
}

function nextDifficulty(d: number, rating: Rating): number {
  const deltaD = -W[6]! * (rating - 3);
  const dPrime = d + deltaD * ((10 - d) / 9); // linear-damping variant
  // Mean reversion toward the Easy-init difficulty.
  return clampD(W[7]! * initDifficulty(4) + (1 - W[7]!) * dPrime);
}

function stabilityAfterRecall(d: number, s: number, r: number, rating: Rating): number {
  const hardPenalty = rating === 2 ? W[15]! : 1;
  const easyBonus = rating === 4 ? W[16]! : 1;
  const inc =
    Math.exp(W[8]!) *
    (11 - d) *
    Math.pow(s, -W[9]!) *
    (Math.exp(W[10]! * (1 - r)) - 1) *
    hardPenalty *
    easyBonus;
  return Math.max(s * (inc + 1), s, MIN_STABILITY);
}

function stabilityAfterLapse(d: number, s: number, r: number): number {
  const sLapse =
    W[11]! *
    Math.pow(d, -W[12]!) *
    (Math.pow(s + 1, W[13]!) - 1) *
    Math.exp(W[14]! * (1 - r));
  return Math.max(Math.min(sLapse, s), MIN_STABILITY);
}

/** Interval (days) to reach `targetRetention` from stability S. */
export function intervalDays(stability: number, targetRetention = 0.9): number {
  const ivl = (stability / FACTOR) * (Math.pow(targetRetention, 1 / DECAY) - 1);
  return Math.max(1, Math.round(ivl));
}

/**
 * Apply a grade to a card, returning the new state. Pure function — caller
 * persists the result. `now` is epoch ms (injected for testability).
 */
export function review(
  prev: CardState | null,
  clueId: number,
  grade: Grade,
  now: number,
  targetRetention = 0.9,
): CardState {
  const rating = gradeToRating(grade);

  if (prev === null || prev.reps === 0) {
    const difficulty = initDifficulty(rating);
    const stability = initStability(rating);
    const ivl = grade === Grade.Missed ? 0 : intervalDays(stability, targetRetention);
    return {
      clueId,
      stability,
      difficulty,
      due: now + Math.max(ivl, grade === Grade.Missed ? 1 / 1440 : ivl) * DAY_MS,
      lastReview: now,
      reps: 1,
      lapses: grade === Grade.Missed ? 1 : 0,
      lastGrade: grade,
    };
  }

  const elapsedDays = Math.max(0, (now - prev.lastReview) / DAY_MS);
  const r = retrievability(elapsedDays, prev.stability);
  const difficulty = nextDifficulty(prev.difficulty, rating);

  let stability: number;
  let lapses = prev.lapses;
  if (grade === Grade.Missed) {
    stability = stabilityAfterLapse(prev.difficulty, prev.stability, r);
    lapses += 1;
  } else {
    stability = stabilityAfterRecall(prev.difficulty, prev.stability, r, rating);
  }

  // A miss re-enters short-term relearning (~10 min); otherwise schedule ahead.
  const due =
    grade === Grade.Missed
      ? now + 10 * 60 * 1000
      : now + intervalDays(stability, targetRetention) * DAY_MS;

  return {
    clueId,
    stability,
    difficulty,
    due,
    lastReview: now,
    reps: prev.reps + 1,
    lapses,
    lastGrade: grade,
  };
}
